import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import Navbar from '@/components/layout/Navbar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  Loader2,
  ShieldCheck,
  Shield,
  ShieldAlert,
  ExternalLink,
  Calendar,
  MapPin,
  User as UserIcon,
  CheckCircle2,
  XCircle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const prettyDate = (iso) => {
  if (!iso) return null;
  try {
    const d = parseISO(iso);
    if (!isValid(d)) return iso;
    return format(d, 'EEE d MMM yyyy');
  } catch {
    return iso;
  }
};

const prettyFlagTime = (iso) => {
  if (!iso) return '';
  try {
    const d = parseISO(iso);
    if (!isValid(d)) return iso;
    return format(d, "d MMM 'at' HH:mm");
  } catch {
    return iso;
  }
};

const AdminEventModerationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [rejectTarget, setRejectTarget] = useState(null);  // event being rejected
  const [rejectReason, setRejectReason] = useState('');
  const [submittingId, setSubmittingId] = useState(null);

  const authHeaders = () => {
    const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  // Polled fetch — setState only via post-effect callback so the strict
  // React-19 `set-state-in-effect` rule stays happy. Cancellation flag
  // prevents racing setState calls after unmount.
  useEffect(() => {
    let cancelled = false;
    const fetchPending = async () => {
      try {
        const res = await axios.get(`${API}/admin/events/pending-review`, { headers: authHeaders() });
        if (cancelled) return;
        setEvents(res.data?.events || []);
      } catch (err) {
        if (cancelled) return;
        toast.error(err?.response?.data?.detail || 'Could not load pending events');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPending();
    const id = setInterval(fetchPending, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const approve = async (eventId) => {
    setSubmittingId(eventId);
    try {
      await axios.post(`${API}/admin/events/${eventId}/review/approve`, {}, { headers: authHeaders() });
      toast.success('Event approved');
      setEvents((cur) => cur.filter((e) => e.event_id !== eventId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not approve event');
    } finally {
      setSubmittingId(null);
    }
  };

  const openReject = (event) => {
    setRejectTarget(event);
    setRejectReason('');
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    const id = rejectTarget.event_id;
    setSubmittingId(id);
    try {
      await axios.post(
        `${API}/admin/events/${id}/review/reject`,
        { notes: rejectReason || null, hide_event: true },
        { headers: authHeaders() },
      );
      toast.success('Event rejected and hidden from public');
      setEvents((cur) => cur.filter((e) => e.event_id !== id));
      setRejectTarget(null);
      setRejectReason('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reject event');
    } finally {
      setSubmittingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="admin-event-moderation-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 pt-24 pb-12">

        <div className="flex items-center justify-between mb-6 gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="moderation-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            Event Moderation
          </h1>
          <div className="w-[78px]" />
        </div>

        <Card className="bg-card/40 border-border/30 mb-5">
          <CardContent className="p-4 text-sm text-muted-foreground">
            <p className="flex items-start gap-2">
              <Shield className="w-4 h-4 mt-0.5 text-emerald-400 flex-shrink-0" />
              <span>
                User-submitted events go live immediately so organisers don&apos;t wait. They land here
                for a safety check — approve to keep them visible, or reject to hide them from the public.
              </span>
            </p>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : events.length === 0 ? (
          <Card className="bg-card/60 border-border/40">
            <CardContent className="p-10 text-center space-y-3" data-testid="moderation-empty">
              <ShieldCheck className="w-10 h-10 mx-auto text-emerald-400" />
              <p className="font-medium">No events waiting for review.</p>
              <p className="text-sm text-muted-foreground">You&apos;re all caught up — nice one.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {events.map((ev) => {
              const organiser = ev.organiser_profile || {};
              const flaggedAt = ev.admin_review?.flagged_at;
              const loc = ev.location || {};
              return (
                <Card
                  key={ev.event_id}
                  className="bg-card/60 border-border/50 overflow-hidden"
                  data-testid={`moderation-event-${ev.event_id}`}
                >
                  <CardContent className="p-0">
                    <div className="flex flex-col sm:flex-row">
                      {ev.image_url || ev.flyer_url ? (
                        <div className="sm:w-44 sm:flex-shrink-0 bg-black/40">
                          <img
                            src={ev.image_url || ev.flyer_url}
                            alt={ev.name}
                            className="w-full h-44 sm:h-full object-cover"
                          />
                        </div>
                      ) : null}
                      <div className="flex-1 p-4 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <h2 className="font-semibold text-base leading-tight truncate" title={ev.name}>
                              {ev.name || 'Untitled event'}
                            </h2>
                            {flaggedAt && (
                              <p className="text-[11px] text-muted-foreground mt-0.5">
                                Posted {prettyFlagTime(flaggedAt)}
                              </p>
                            )}
                          </div>
                          <Badge variant="outline" className="border-amber-700/60 text-amber-300 text-[10px]">
                            <ShieldAlert className="w-3 h-3 mr-1" />
                            Awaiting review
                          </Badge>
                        </div>

                        <div className="text-xs space-y-1 text-muted-foreground">
                          {ev.date && (
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5" />
                              <span>{prettyDate(ev.date)}</span>
                            </div>
                          )}
                          {loc.address && (
                            <div className="flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5" />
                              <span className="truncate">{loc.address}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1.5">
                            <UserIcon className="w-3.5 h-3.5" />
                            <span className="truncate">
                              {organiser.name || organiser.username || ev.organiser_name || 'Unknown organiser'}
                              {organiser.community_verified ? (
                                <span className="ml-1 text-emerald-400">· verified</span>
                              ) : null}
                              {organiser.email ? (
                                <span className="ml-1 opacity-60">· {organiser.email}</span>
                              ) : null}
                            </span>
                          </div>
                        </div>

                        {ev.description && (
                          <p className="text-xs text-muted-foreground line-clamp-3 whitespace-pre-wrap">
                            {ev.description}
                          </p>
                        )}

                        <div className="flex flex-wrap gap-2 pt-1">
                          <Button
                            asChild
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            data-testid={`open-event-${ev.event_id}`}
                          >
                            <a
                              href={`/events/${ev.event_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink className="w-3.5 h-3.5 mr-1" />
                              Open
                            </a>
                          </Button>
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-500 text-white"
                            onClick={() => approve(ev.event_id)}
                            disabled={submittingId === ev.event_id}
                            data-testid={`approve-event-${ev.event_id}`}
                          >
                            {submittingId === ev.event_id ? (
                              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                            ) : (
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                            )}
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => openReject(ev)}
                            disabled={submittingId === ev.event_id}
                            data-testid={`reject-event-${ev.event_id}`}
                          >
                            <XCircle className="w-3.5 h-3.5 mr-1" />
                            Reject &amp; hide
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Reject confirmation */}
      <Dialog open={!!rejectTarget} onOpenChange={(open) => { if (!open) setRejectTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="reject-event-dialog">
          <DialogHeader>
            <DialogTitle>Reject this event?</DialogTitle>
            <DialogDescription>
              It will be hidden from public listings and search. You can add an internal note for the record.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm font-medium truncate" title={rejectTarget?.name}>
            {rejectTarget?.name}
          </div>
          <Textarea
            placeholder="Reason (optional, internal-only)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={3}
            data-testid="reject-reason-textarea"
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setRejectTarget(null)}
              data-testid="reject-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={submitReject}
              disabled={submittingId === rejectTarget?.event_id}
              data-testid="reject-confirm-btn"
            >
              {submittingId === rejectTarget?.event_id ? (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              ) : (
                <XCircle className="w-3.5 h-3.5 mr-1" />
              )}
              Reject &amp; hide
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEventModerationPage;
